import React, { useEffect, useState, useMemo } from "react";
import { CheckBoxInput } from "../common/Buttons";
import { IconColumnSpacing } from "../Layout/Icons";
import styled from "styled-components";
import { ButtonRoundedSort, ButtonSettings } from "../buttons";
import RetailProductRow from "./RetailProductRow";
import useDebounce from "../../hooks/useDebounce";
import { useSelector } from "react-redux";
import { Tooltip } from "antd";
import { AiOutlineInfoCircle } from "react-icons/ai";

const STORE_COLORS = [
  "rgba(255, 206, 49, 0.12)",
  "rgba(200, 82, 96, 0.12)",
  "rgba(66, 173, 226, 0.12)",
  "rgb(0 150 136 / 14%)",
  "rgba(156, 91, 209, 0.12)",
  "rgba(255, 152, 0, 0.12)",
  "rgba(121, 85, 72, 0.12)",
  "rgba(63, 81, 181, 0.12)",
  "rgba(139, 195, 74, 0.14)",
  "rgba(233, 30, 99, 0.10)",
];

const columnsWithPermissions = [
  "SKU",
  "Prod. cat.",
  "Brand",
  "MPN",
  "EAN",
  "Prime cost",
  "Technorama.lt",
  "RRP",
  "Failure",
  "Old price",
  "New price",
  "New margin",
  "Price change",
  "Cheapest price",
  "Store prices",
];

// retail_columns in localStorage is a whitelist of visible columns, so a newly added column would
// stay hidden for everyone who already has a saved list. Enable each new column exactly once —
// after that the user's own show/hide choice wins.
const COLUMNS_ADDED_SINCE = ["Failure", "RRP", "Technorama.lt"];

const withNewColumnsEnabled = (savedColumns) => {
  if (!Array.isArray(savedColumns)) return columnsWithPermissions;

  const alreadyIntroduced =
    JSON.parse(localStorage.getItem("retail_columns_introduced")) || [];
  const toIntroduce = COLUMNS_ADDED_SINCE.filter(
    (column) => !alreadyIntroduced.includes(column)
  );
  if (!toIntroduce.length) return savedColumns;

  localStorage.setItem(
    "retail_columns_introduced",
    JSON.stringify([...alreadyIntroduced, ...toIntroduce])
  );
  // Keep the configured column order, not the order they happened to be enabled in.
  const next = [...savedColumns, ...toIntroduce];
  return columnsWithPermissions.filter((column) => next.includes(column));
};

const InfoTooltipIcon = ({ title, placement = "top" }) => (
  <Tooltip placement={placement} title={title}>
    <span className="ml-1" style={{ cursor: "help" }}>
      <AiOutlineInfoCircle color="#1890FF" size={13} />
    </span>
  </Tooltip>
);

const RetailProductTable = ({
  data,
  checked,
  setChecked,
  competitor,
  page,
  loading,
  lastBookElementRef,
  table_sort,
  setFilters,
  hasMore,
  imum_product_tags,
  filterString,
  retail_product_rules,
  failed,
  totalLength,
  departmentOptions = [],
  selectedDepartments = [],
}) => {
  const [showColumns, setShowColumns] = useState(() =>
    withNewColumnsEnabled(JSON.parse(localStorage.getItem("retail_columns")))
  );
  const [tableHeight, setTableHeight] = useState(0);
  const [active, setActive] = useState(false);
  const [columnWidthMain, setColumnWidthMain] = useState(
    JSON.parse(localStorage.getItem("retail_prod_title_column_width")) || 288
  );

  useEffect(() => {
    document.addEventListener("keydown", function (event) {
      if (event.key == "Escape") return setActive(false);
    });
  }, []);

  const storeNames = useMemo(() => {
    if (!data || data.length === 0) return [];
    const names = [];
    data.forEach((d) => {
      (d.prices || []).filter((p) => p.price_type === "new").forEach((p) => {
        if (!names.includes(p.department)) names.push(p.department);
      });
      (d.product_supplier_prices || []).forEach((p) => {
        if (p.department && !names.includes(p.department)) names.push(p.department);
      });
      (d.today_supplier_prices || []).forEach((p) => {
        if (p.department && !names.includes(p.department)) names.push(p.department);
      });
    });
    return names;
  }, [data]);

  // Columns must come from the department list, not from the loaded page — a product with
  // stock in a department that has no price/supplier row still needs its column, and the
  // column set has to stay identical (and in the same order) while paging or filtering.
  const filteredStores = useMemo(() => {
    const departments = (departmentOptions || [])
      .map((o) => o.value)
      .filter(Boolean);
    // storeNames is only a fallback for when /vv_retail_stores fails to load.
    const allStores = departments.length ? departments : storeNames;
    if (!selectedDepartments?.length) return allStores;
    const selected = allStores.filter((name) =>
      selectedDepartments.includes(name)
    );
    return selected.length ? selected : allStores;
  }, [departmentOptions, selectedDepartments, storeNames]);

  const storeColorMap = useMemo(() => {
    const map = {};
    filteredStores.forEach((name, idx) => {
      map[name] = STORE_COLORS[idx % STORE_COLORS.length];
    });
    return map;
  }, [filteredStores]);

  const availableShops = useMemo(() => {
    if (!data || data.length === 0) return [];
    const seen = new Set();
    const shops = [];
    data.forEach((d) => {
      (d.today_supplier_prices || []).forEach((tsp) => {
        const key = `${tsp.category}|${tsp.shop}`;
        if (seen.has(key)) return;
        seen.add(key);
        shops.push({ shop: tsp.shop, country: tsp.category });
      });
    });
    return shops;
  }, [data]);

  useEffect(() => {
    const height = () => {
      let elementPosition = 0;
      if (document.querySelector("hr")?.getBoundingClientRect()?.top)
        elementPosition = document
          .querySelector(".table-responsive")
          ?.getBoundingClientRect()?.top;
      if (!hasMore && elementPosition)
        return window.innerHeight - elementPosition - 36;
      return window.innerHeight - elementPosition - 5;
    };
    setTableHeight(height());
  }, [loading, hasMore, data.length, filteredStores.length]);

  const showColumnAvailable = (val) => showColumns.includes(val);

  const showOldPrice = showColumnAvailable("Old price");
  const showNewPrice = showColumnAvailable("New price");
  const showNewMargin = showColumnAvailable("New margin");
  const showPriceChange = showColumnAvailable("Price change");
  const showCheapestPrice = showColumnAvailable("Cheapest price");
  const showStorePrices = showColumnAvailable("Store prices");

  return (
    <div
      className="table-responsive"
      style={{ height: tableHeight }}
    >
      <TableLayout className="table table-hover mt-3" sticky={true}>
        <thead>
          <tr className="text-muted">
            <ColumnProdTitle
              loading={loading}
              checked={checked}
              d={{ title: "Prod. title" }}
              data={data}
              page={page}
              setChecked={setChecked}
              columnWidthMain={columnWidthMain}
              setColumnWidthMain={setColumnWidthMain}
              table_sort={table_sort}
              setFilters={setFilters}
              filterString={filterString}
              totalLength={totalLength}
              rowSpan={2}
            />
            {showColumnAvailable("SKU") && (
              <th
                className="bg-white align-middle text-black"
                rowSpan={2}
                style={stickyHeaderStyle}
              >
                SKU
              </th>
            )}
            {showColumnAvailable("MPN") && (
              <th
                className="bg-white align-middle text-black"
                rowSpan={2}
                style={stickyHeaderStyle}
              >
                MPN
              </th>
            )}
            {showColumnAvailable("EAN") && (
              <th
                className="bg-white align-middle text-black"
                rowSpan={2}
                style={stickyHeaderStyle}
              >
                EAN
              </th>
            )}
            {showColumnAvailable("Prod. cat.") && (
              <th
                className="bg-white align-middle text-black"
                rowSpan={2}
                style={stickyHeaderStyle}
              >
                <nobr>Prod. cat.</nobr>
              </th>
            )}
            {showColumnAvailable("Brand") && (
              <th
                className="bg-white align-middle text-black"
                rowSpan={2}
                style={stickyHeaderStyle}
              >
                Brand
              </th>
            )}
            {showColumnAvailable("Prime cost") && (
              <th
                className="bg-white align-middle text-black"
                rowSpan={2}
                style={stickyHeaderStyle}
              >
                <nobr>
                  Prime cost
                  <InfoTooltipIcon title="Included VAT" />
                </nobr>
              </th>
            )}
            {showColumnAvailable("Technorama.lt") && (
              <th
                className="bg-white align-middle text-black"
                rowSpan={2}
                style={stickyHeaderStyle}
              >
                <nobr>
                  Technorama.lt
                  <InfoTooltipIcon title="Included VAT" />
                </nobr>
              </th>
            )}
            {showColumnAvailable("RRP") && (
              <th
                className="bg-white align-middle text-black"
                rowSpan={2}
                style={stickyHeaderStyle}
              >
                <nobr>
                  RRP
                  <InfoTooltipIcon title="Included VAT" />
                </nobr>
              </th>
            )}
            {showColumnAvailable("Failure") && (
              <th
                className="bg-white align-middle text-black"
                rowSpan={2}
                style={stickyHeaderStyle}
              >
                <nobr>Failure</nobr>
              </th>
            )}
            <th
              className="bg-white align-middle text-black"
              rowSpan={2}
              style={{ ...stickyHeaderStyle, zIndex: 20 }}
            >
              <ButtonSettings
                columns_type="retail_columns"
                columns={columnsWithPermissions}
                showColumns={showColumns}
                setShowColumns={setShowColumns}
              />
            </th>
            {showOldPrice && filteredStores.length > 0 && (
              <th
                className="bg-white align-middle text-black text-center"
                colSpan={filteredStores.length}
                style={{
                  ...stickyHeaderStyle,
                  borderLeft: "1px solid #BFBFBF",
                  borderRight: "1px solid #BFBFBF",
                }}
              >
                <div className="font-semi-bold text-sm">Old price<InfoTooltipIcon title="Included VAT" /></div>
              </th>
            )}
            {showNewPrice && filteredStores.length > 0 && (
              <th
                className="bg-white align-middle text-black text-center"
                colSpan={filteredStores.length}
                style={{
                  ...stickyHeaderStyle,
                  borderLeft: "1px solid #BFBFBF",
                  borderRight: "1px solid #BFBFBF",
                }}
              >
                <div className="font-semi-bold text-sm">New price<InfoTooltipIcon title="Included VAT" /></div>
              </th>
            )}
            {showNewMargin && filteredStores.length > 0 && (
              <th
                className="bg-white align-middle text-black text-center"
                colSpan={filteredStores.length}
                style={{
                  ...stickyHeaderStyle,
                  borderLeft: "1px solid #BFBFBF",
                  borderRight: "1px solid #BFBFBF",
                }}
              >
                <div className="font-semi-bold text-sm">New margin</div>
              </th>
            )}
            {showPriceChange && filteredStores.length > 0 && (
              <th
                className="bg-white align-middle text-black text-center"
                colSpan={filteredStores.length}
                style={{
                  ...stickyHeaderStyle,
                  borderLeft: "1px solid #BFBFBF",
                  borderRight: "1px solid #BFBFBF",
                }}
              >
                <div className="font-semi-bold text-sm">Price change</div>
              </th>
            )}
            {showCheapestPrice && filteredStores.length > 0 && (
              <th
                className="bg-white align-middle text-black text-center"
                colSpan={filteredStores.length}
                style={{
                  ...stickyHeaderStyle,
                  borderLeft: "1px solid #BFBFBF",
                  borderRight: "1px solid #BFBFBF",
                }}
              >
                <div className="font-semi-bold text-sm">Cheapest price</div>
              </th>
            )}
            {showStorePrices && availableShops.length > 0 && (
              <th
                className="bg-white align-middle text-black text-center"
                colSpan={availableShops.length}
                style={{
                  ...stickyHeaderStyle,
                  borderLeft: "1px solid #BFBFBF",
                  borderRight: "1px solid #BFBFBF",
                }}
              >
                <div className="font-semi-bold text-sm">Store prices<InfoTooltipIcon title="Included VAT" /></div>
              </th>
            )}
          </tr>
          <tr className="text-muted">
            {showOldPrice &&
              filteredStores.map((store, idx) => (
                <th
                  key={`old_${store}`}
                  className="align-middle text-black text-center"
                  style={{
                    ...stickyHeaderStyle,
                    top: 36,
                    backgroundColor: storeColorMap[store],
                    borderLeft: idx === 0 ? "1px solid #BFBFBF" : "none",
                    borderRight: idx === filteredStores.length - 1 ? "1px solid #BFBFBF" : "none",
                    minWidth: 100,
                    whiteSpace: "nowrap",
                    fontSize: "0.75rem",
                  }}
                >
                  {store}
                </th>
              ))}
            {showNewPrice &&
              filteredStores.map((store, idx) => (
                <th
                  key={`new_${store}`}
                  className="align-middle text-black text-center"
                  style={{
                    ...stickyHeaderStyle,
                    top: 36,
                    backgroundColor: storeColorMap[store],
                    borderLeft: idx === 0 ? "1px solid #BFBFBF" : "none",
                    borderRight: idx === filteredStores.length - 1 ? "1px solid #BFBFBF" : "none",
                    minWidth: 100,
                    whiteSpace: "nowrap",
                    fontSize: "0.75rem",
                  }}
                >
                  {store}
                </th>
              ))}
            {showNewMargin &&
              filteredStores.map((store, idx) => (
                <th
                  key={`margin_${store}`}
                  className="align-middle text-black text-center"
                  style={{
                    ...stickyHeaderStyle,
                    top: 36,
                    backgroundColor: storeColorMap[store],
                    borderLeft: idx === 0 ? "1px solid #BFBFBF" : "none",
                    borderRight: idx === filteredStores.length - 1 ? "1px solid #BFBFBF" : "none",
                    minWidth: 100,
                    whiteSpace: "nowrap",
                    fontSize: "0.75rem",
                  }}
                >
                  {store}
                </th>
              ))}
            {showPriceChange &&
              filteredStores.map((store, idx) => (
                <th
                  key={`change_${store}`}
                  className="align-middle text-black text-center"
                  style={{
                    ...stickyHeaderStyle,
                    top: 36,
                    backgroundColor: storeColorMap[store],
                    borderLeft: idx === 0 ? "1px solid #BFBFBF" : "none",
                    borderRight: idx === filteredStores.length - 1 ? "1px solid #BFBFBF" : "none",
                    minWidth: 100,
                    whiteSpace: "nowrap",
                    fontSize: "0.75rem",
                  }}
                >
                  {store}
                </th>
              ))}
            {showCheapestPrice &&
              filteredStores.map((store, idx) => (
                <th
                  key={`cheapest_${store}`}
                  className="align-middle text-black text-center"
                  style={{
                    ...stickyHeaderStyle,
                    top: 36,
                    backgroundColor: storeColorMap[store],
                    borderLeft: idx === 0 ? "1px solid #BFBFBF" : "none",
                    borderRight: idx === filteredStores.length - 1 ? "1px solid #BFBFBF" : "none",
                    minWidth: 100,
                    whiteSpace: "nowrap",
                    fontSize: "0.75rem",
                  }}
                >
                  {store}
                </th>
              ))}
            {showStorePrices &&
              availableShops.map((s, idx) => (
                <th
                  key={`shop_${s.country}_${s.shop}`}
                  className="align-middle text-black text-center"
                  style={{
                    ...stickyHeaderStyle,
                    top: 36,
                    borderLeft: idx === 0 ? "1px solid #BFBFBF" : "none",
                    borderRight: idx === availableShops.length - 1 ? "1px solid #BFBFBF" : "none",
                    minWidth: 100,
                    whiteSpace: "nowrap",
                    fontSize: "0.75rem",
                  }}
                >
                  {s.shop}
                </th>
              ))}
          </tr>
        </thead>
        <tbody>
          {data.map((d, idx) => {
            const checked_boolean = checked.includes(d.id);
            return (
              <RetailProductRow
                key={`${d.id}_${idx}`}
                {...d}
                stores={filteredStores}
                storeColorMap={storeColorMap}
                availableShops={availableShops}
                showColumnAvailable={showColumnAvailable}
                lastBookElementRef={
                  data.length === idx + 1 ? lastBookElementRef : null
                }
                setChecked={() => {
                  setChecked((prev) => {
                    if (checked_boolean)
                      return prev.filter((pr) => pr !== d.id);
                    return [...new Set([...prev, d.id])];
                  });
                }}
                checked={checked_boolean}
                active={active}
                setActive={setActive}
                imum_product_tags={imum_product_tags}
                columnWidthMain={columnWidthMain}
                retail_product_rules={retail_product_rules}
                failed={failed}
              />
            );
          })}
        </tbody>
      </TableLayout>
    </div>
  );
};

export default RetailProductTable;

const stickyHeaderStyle = {
  borderBottom: "1px solid #BFBFBF",
  position: "sticky",
  top: 0,
  zIndex: 15,
};

const ColumnProdTitle = ({
  loading,
  checked,
  d,
  data,
  page,
  setChecked,
  columnWidthMain,
  setColumnWidthMain,
  table_sort,
  setFilters,
  rowSpan,
}) => {
  const [columnWidth, setColumnWidth] = useState(columnWidthMain);
  const [isDragging, setIsDragging] = useState(false);
  const { totals } = useSelector((state) => state.retail);

  useDebounce(
    () => {
      setColumnWidthMain(columnWidth);
      localStorage.setItem("retail_prod_title_column_width", columnWidth);
    },
    600,
    [columnWidth]
  );

  const all_checked = useMemo(() => {
    if (loading) return false;
    if (checked.length == 0) return false;
    if (data.every((d) => checked.includes(d.id))) return true;
    return false;
  }, [loading, checked?.length, data?.length, page]);

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const newWidth = columnWidth + e.movementX;
    if (288 >= newWidth) return;
    setColumnWidth(newWidth);
  };

  return (
    <th
      className="bg-white align-middle text-black p-left"
      style={{
        borderBottom: "1px solid #BFBFBF",
        position: "sticky",
        top: 0,
        zIndex: 20,
      }}
      colSpan={1}
      rowSpan={rowSpan}
      onMouseDown={() => setIsDragging(true)}
      onMouseUp={() => setIsDragging(false)}
      onMouseLeave={() => setIsDragging(false)}
      onMouseMove={handleMouseMove}
    >
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ width: `${columnWidth}px`, cursor: "grab" }}
      >
        <CheckBoxInput
          text={`(${checked.length}) ${d.title}`}
          checked={all_checked}
          onChange={() => {
            setChecked((prev) => {
              let obj = [...prev];
              const all_data = [];
              data.forEach((d) => all_data.push(d.id));
              if (all_checked) {
                return prev.filter((d) => !all_data.includes(d));
              }
              return [...new Set([...obj, ...all_data])];
            });
          }}
        />
        <div>
          <ButtonRoundedSort
            active={
              (table_sort?.type == "product_name" && table_sort?.sort) || ""
            }
            setActive={(sort) => {
              setFilters({
                name: "table_sort",
                value: {
                  ...table_sort,
                  lang: " ",
                  sort,
                  type: sort == "" ? "" : "product_name",
                },
              });
            }}
          />
        </div>
        <div className="flex-1 d-flex align-items-center justify-content-end">
          <IconColumnSpacing
            className="m-1 mr-2"
            style={{ width: 15, height: 15 }}
          />
        </div>
      </div>
    </th>
  );
};

const TableLayout = styled.table`
  ${(props) =>
    props.sticky &&
    `
tr > th:first-child,
tr > td:first-child {
  position: sticky !important;
  left: 0;
  background-color: #fff;
  z-index: 19;
}
tr:nth-child(odd) > td:first-child {
    background-color: #fff;
  }
  tr:nth-child(even) > td:first-child {
    background-color: #f9f9f9;
  }
  `}
`;
